import { useState } from "react";
import { api, formatApiError } from "@/lib/api";
import { toast } from "sonner";

const DENOMINATIONS = [
  { value: 25,  tiles: 250,  tag: "TASTER" },
  { value: 50,  tiles: 550,  tag: "+10% BONUS" },
  { value: 100, tiles: 1150, tag: "+15% BONUS" },
  { value: 250, tiles: 3000, tag: "+20% BONUS" },
];

const fmtGBP = (a) =>
  new Intl.NumberFormat("en-GB", { style: "currency", currency: "GBP", maximumFractionDigits: 0 }).format(a);

export default function GiftCardDialog({ open, onClose }) {
  const [denom, setDenom] = useState(50);
  const [recipientName, setRecipientName] = useState("");
  const [recipientEmail, setRecipientEmail] = useState("");
  const [senderName, setSenderName] = useState("");
  const [message, setMessage] = useState("");
  const [busy, setBusy] = useState(false);

  if (!open) return null;
  const selected = DENOMINATIONS.find(d => d.value === denom);

  const submit = async (e) => {
    e.preventDefault();
    if (!recipientName.trim() || !senderName.trim()) {
      toast.error("Sender and recipient names are required");
      return;
    }
    setBusy(true);
    try {
      const { data } = await api.post("/gifts/checkout", {
        denomination: denom,
        recipient_name: recipientName.trim(),
        recipient_email: recipientEmail.trim() || null,
        sender_name: senderName.trim(),
        message: message.trim(),
        origin_url: window.location.origin,
      });
      sessionStorage.setItem("dr_pending_gift_code", data.code);
      window.location.href = data.url;
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Couldn't open checkout");
      setBusy(false);
    }
  };

  return (
    <div className="dr-modal" onClick={onClose} data-testid="gift-dialog">
      <div className="dr-modal-card" onClick={(e) => e.stopPropagation()}>
        <div className="dr-modal-eyebrow">DIGITAL GIFT PASS</div>
        <h2 className="dr-modal-title">Send a Gift</h2>
        <div className="dr-modal-rule" />

        <p className="gift-blurb">
          A digital pass redeemable for tiles across all venues. Recipient enters
          the code in the app — bonus tiles included on £50+.
        </p>

        <form onSubmit={submit} className="dr-form">
          <div className="dr-field">
            <label>AMOUNT</label>
            <div className="gift-denoms" data-testid="gift-denoms">
              {DENOMINATIONS.map(d => (
                <button
                  key={d.value}
                  type="button"
                  data-testid={`gift-denom-${d.value}`}
                  className={`gift-denom ${denom === d.value ? "active" : ""}`}
                  onClick={() => setDenom(d.value)}
                >
                  <div className="gift-denom-amt">{fmtGBP(d.value)}</div>
                  <div className="gift-denom-tiles">{d.tiles} TILES</div>
                  <div className="gift-denom-tag">{d.tag}</div>
                </button>
              ))}
            </div>
          </div>

          <div className="dr-row-2">
            <div className="dr-field">
              <label>FROM</label>
              <input
                data-testid="gift-sender"
                type="text"
                value={senderName}
                onChange={(e) => setSenderName(e.target.value)}
                placeholder="Your name"
                required
              />
            </div>
            <div className="dr-field">
              <label>TO</label>
              <input
                data-testid="gift-recipient"
                type="text"
                value={recipientName}
                onChange={(e) => setRecipientName(e.target.value)}
                placeholder="Recipient name"
                required
              />
            </div>
          </div>

          <div className="dr-field">
            <label>RECIPIENT EMAIL <span style={{ opacity: 0.5 }}>(OPTIONAL)</span></label>
            <input
              data-testid="gift-recipient-email"
              type="email"
              value={recipientEmail}
              onChange={(e) => setRecipientEmail(e.target.value)}
              placeholder="for delivery via Resend (when configured)"
            />
          </div>

          <div className="dr-field">
            <label>MESSAGE <span style={{ opacity: 0.5 }}>(OPTIONAL)</span></label>
            <textarea
              data-testid="gift-message"
              value={message}
              onChange={(e) => setMessage(e.target.value)}
              rows={2}
              maxLength={240}
              placeholder="Happy birthday — bring me back some dim sum…"
            />
          </div>

          <div className="dr-actions">
            <button
              data-testid="gift-cancel-btn"
              type="button"
              className="dr-btn-ghost"
              onClick={onClose}
            >
              CANCEL
            </button>
            <button
              data-testid="gift-pay-btn"
              type="submit"
              className="dr-btn-primary"
              disabled={busy}
            >
              {busy ? "REDIRECTING…" : `PAY ${fmtGBP(selected.value)}`}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
